import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Award, ChevronRight, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { formatSsLabel } from '../utils/unitDisplay';
import { sortBySsCode } from '../utils/ssCodeSort';
import { InfoButton } from './InfoModal';

function isIncluded(inst) {
  const v = inst?.include_in_inspection;
  return v === 1 || v === true || v === '1';
}

/**
 * Edit Building — Specified Systems list.
 * Full / Partial radios follow the Owner coverage choice and stay disabled.
 * Unchecking an SS still switches the building to Partial; checking every SS switches it back to Full.
 */
function BuildingSsSection(
  {
    buildingId,
    building,
    ssCodes = [],
    onSsChanged,
    drivenScope = '',
    coverageRev = 0,
    onScopeChange,
  },
  ref
) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showAddSs, setShowAddSs] = useState(false);
  const [newSs, setNewSs] = useState({
    ss_code: '',
    location: '',
    inspection_frequency: 'ANNUAL',
  });
  const [scopeDirty, setScopeDirty] = useState(false);
  const [scope, setScope] = useState('FULL');
  const [checks, setChecks] = useState({});
  const seenCoverageRev = useRef(0);
  const skipSync = useRef(false);
  const stateRef = useRef({ scope: 'FULL', checks: {}, instances: [] });
  const writeChain = useRef(Promise.resolve());

  const instances = useMemo(
    () =>
      sortBySsCode(
        (building?.ssInstances || building?.ss_instances || []).filter(
          (i) => !i.status || i.status === 'ACTIVE'
        )
      ),
    [building]
  );

  stateRef.current = { scope, checks, instances };

  useEffect(() => {
    if (skipSync.current) return;
    const s =
      String(building?.inspection_scope || 'FULL').toUpperCase() === 'PARTIAL' ? 'PARTIAL' : 'FULL';
    setScope(s);
    const next = {};
    instances.forEach((inst) => {
      next[inst.id] = s === 'FULL' ? true : isIncluded(inst);
    });
    setChecks(next);
    setScopeDirty(false);
  }, [building?.inspection_scope, instances]);

  const invalidate = () => {
    queryClient.invalidateQueries(['building', buildingId]);
    queryClient.invalidateQueries(['building', buildingId, 'ss-instances']);
    queryClient.invalidateQueries(['building', buildingId, 'ss-units']);
    queryClient.invalidateQueries(['building', buildingId, 'ss-progress']);
    queryClient.invalidateQueries(['bwof-workflow', buildingId]);
    queryClient.invalidateQueries(['buildings']);
  };

  const addSsMutation = useMutation({
    mutationFn: async (payload) => {
      const response = await api.post(`/buildings/${buildingId}/ss-instances`, payload);
      return response.data;
    },
    onSuccess: () => {
      toast.success('SS added');
      setShowAddSs(false);
      setNewSs({ ss_code: '', location: '', inspection_frequency: 'ANNUAL' });
      invalidate();
      onSsChanged?.();
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to add SS'),
  });

  const deleteSsMutation = useMutation({
    mutationFn: async (instanceId) => {
      await api.delete(`/buildings/${buildingId}/ss-instances/${instanceId}`);
    },
    onSuccess: () => {
      toast.success('SS removed');
      invalidate();
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to remove SS'),
  });

  const saveScopeMutation = useMutation({
    mutationFn: async ({ nextScope, nextChecks }) => {
      const s = nextScope || scope;
      const map = nextChecks || checks;
      await api.put(`/buildings/${buildingId}`, {
        inspection_scope: s,
        agency_scope: s === 'PARTIAL' ? 'SS_ONLY' : 'FULL_AGENT',
        is_bwof_agent: s !== 'PARTIAL',
        ...(s === 'FULL' ? { clear_partly_agent: true, partly_agent_id: null } : {}),
      });
      if (s === 'PARTIAL') {
        await Promise.all(
          instances.map((inst) =>
            api.put(`/buildings/${buildingId}/ss-instances/${inst.id}`, {
              include_in_inspection: map[inst.id] ? 1 : 0,
            })
          )
        );
      }
      return s;
    },
    onSuccess: (s) => {
      toast.success(s === 'FULL' ? 'Full inspection — all SS in scope' : 'Partial inspection scope saved');
      setScopeDirty(false);
      invalidate();
      if (s === 'PARTIAL') onSsChanged?.();
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Save failed'),
  });

  const writeInclude = (nextScope, nextChecks, rows) => {
    const job = writeChain.current.then(async () => {
      const list = rows || [];
      if (nextScope === 'PARTIAL') {
        await Promise.all(
          list.map((inst) =>
            api.put(`/buildings/${buildingId}/ss-instances/${inst.id}`, {
              include_in_inspection: nextChecks[inst.id] ? 1 : 0,
            })
          )
        );
      }
      const serverScope =
        String(building?.inspection_scope || 'FULL').toUpperCase() === 'PARTIAL' ? 'PARTIAL' : 'FULL';
      if (nextScope !== serverScope) {
        await api.put(`/buildings/${buildingId}`, {
          inspection_scope: nextScope,
          agency_scope: nextScope === 'PARTIAL' ? 'SS_ONLY' : 'FULL_AGENT',
          is_bwof_agent: nextScope !== 'PARTIAL',
          ...(nextScope === 'FULL' ? { clear_partly_agent: true, partly_agent_id: null } : {}),
        });
      }
    });
    writeChain.current = job.catch(() => {});
    return job;
  };

  useImperativeHandle(ref, () => ({
    persistScope() {
      const snap = stateRef.current;
      if (snap.scope !== 'PARTIAL') return Promise.resolve();
      skipSync.current = true;
      return writeInclude(snap.scope, snap.checks, snap.instances);
    },
  }));

  const resetScopeUi = () => {
    setScopeDirty(false);
    const s =
      String(building?.inspection_scope || 'FULL').toUpperCase() === 'PARTIAL' ? 'PARTIAL' : 'FULL';
    setScope(s);
    const next = {};
    instances.forEach((inst) => {
      next[inst.id] = s === 'FULL' ? true : isIncluded(inst);
    });
    setChecks(next);
  };

  const selectFull = () => {
    const next = {};
    instances.forEach((inst) => {
      next[inst.id] = true;
    });
    skipSync.current = false;
    setChecks(next);
    setScope('FULL');
    setScopeDirty(false);
    saveScopeMutation.mutate({ nextScope: 'FULL', nextChecks: next });
  };

  /** Partial: clear all ticks — user then ticks which SS are in scope */
  const selectPartial = () => {
    const next = {};
    instances.forEach((inst) => {
      next[inst.id] = false;
    });
    skipSync.current = false;
    setChecks(next);
    setScope('PARTIAL');
    setScopeDirty(false);
    saveScopeMutation.mutate({ nextScope: 'PARTIAL', nextChecks: next });
  };

  useEffect(() => {
    if (!coverageRev || coverageRev === seenCoverageRev.current) return;
    if (drivenScope !== 'FULL' && drivenScope !== 'PARTIAL') return;
    seenCoverageRev.current = coverageRev;
    if (drivenScope === 'FULL') selectFull();
    else selectPartial();
  }, [coverageRev, drivenScope]);

  const onCheckChange = (instId, checked) => {
    const next = { ...checks, [instId]: checked };
    setChecks(next);
    const allOn =
      instances.length > 0 && instances.every((inst) => !!next[inst.id]);

    if (allOn) {
      skipSync.current = false;
      stateRef.current = { scope: 'FULL', checks: next, instances };
      setScope('FULL');
      setScopeDirty(false);
      onScopeChange?.('FULL');
      saveScopeMutation.mutate({ nextScope: 'FULL', nextChecks: next });
      return;
    }

    skipSync.current = true;
    stateRef.current = { scope: 'PARTIAL', checks: next, instances };
    setScope('PARTIAL');
    setScopeDirty(false);
    onScopeChange?.('PARTIAL');
    writeInclude('PARTIAL', next, instances).catch((e) => {
      toast.error(e.response?.data?.error || 'Could not save the SS selection');
    });
  };

  const handleAdd = () => {
    if (!newSs.ss_code || !newSs.location?.trim()) {
      toast.error('SS code and location are required');
      return;
    }
    addSsMutation.mutate({
      ss_code: newSs.ss_code,
      location: newSs.location.trim(),
      inspection_frequency: newSs.inspection_frequency || null,
      compliance_schedule_number: building?.compliance_schedule_number || null,
      include_in_inspection: scope === 'FULL' ? 1 : 0,
    });
  };

  const busy = saveScopeMutation.isPending;

  return (
    <div id="specified-systems" className="bg-green-100 rounded-xl border-2 border-green-300 overflow-hidden shadow-sm">
      <div className="px-5 py-4 border-b-2 border-green-300 bg-green-100 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold text-gray-900 flex flex-wrap items-center gap-2">
            <Award className="w-5 h-5 text-primary-600" />
            Specified Systems (SS)
            <InfoButton title="Specified Systems (SS)">
              <p>
                Full or Partial is chosen under Owner / Customer. These radios follow that choice and
                cannot be changed here.
              </p>
              <p>
                Uncheck any SS while Full and the building switches to Partial. Tick every SS and it
                switches back to Full. On Partial, the SS you tick are saved straight away, and Update
                Building saves them again.
              </p>
              <p>Click an SS row for CS settings and Default IQP.</p>
            </InfoButton>
          </h2>
        </div>
        <div className="flex flex-wrap gap-2">
          {scope === 'PARTIAL' && scopeDirty ? (
            <>
              <button
                type="button"
                onClick={resetScopeUi}
                disabled={busy}
                className="px-3 py-1.5 text-sm border border-green-300 rounded-lg bg-green-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => saveScopeMutation.mutate({ nextScope: 'PARTIAL', nextChecks: checks })}
                className="px-3 py-1.5 text-sm font-semibold rounded-lg bg-primary-600 text-white disabled:opacity-50"
              >
                {busy ? 'Saving…' : 'Save scope'}
              </button>
            </>
          ) : null}
          <button
            type="button"
            onClick={() => setShowAddSs(!showAddSs)}
            className="inline-flex items-center px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <Plus className="w-4 h-4 mr-1" />
            {showAddSs ? 'Cancel add' : 'Add SS'}
          </button>
        </div>
      </div>

      <div className="p-4 space-y-3">
        <fieldset
          disabled
          className="rounded-lg border border-green-300 bg-green-100 px-3 py-2.5 flex flex-wrap gap-6"
        >
          <legend className="text-xs font-semibold text-gray-700 px-1">Inspection scope</legend>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input type="radio" name="inspScope" checked={scope === 'FULL'} readOnly />
            Full
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input type="radio" name="inspScope" checked={scope === 'PARTIAL'} readOnly />
            Partial
          </label>
          <p className="w-full text-[11px] text-gray-500 m-0">
            Set under Owner / Customer. Unchecking an SS still switches this to Partial.
          </p>
        </fieldset>

        {showAddSs && (
          <div className="p-3 bg-green-100 rounded-lg border border-green-300 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">SS Code *</label>
                <select
                  value={newSs.ss_code}
                  onChange={(e) => setNewSs({ ...newSs, ss_code: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                >
                  <option value="">Select SS code</option>
                  {ssCodes.map((ss) => (
                    <option key={ss.code} value={ss.code}>
                      {ss.code} — {ss.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Location *</label>
                <input
                  type="text"
                  value={newSs.location}
                  onChange={(e) => setNewSs({ ...newSs, location: e.target.value })}
                  placeholder="e.g. Level 1"
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Frequency</label>
                <select
                  value={newSs.inspection_frequency}
                  onChange={(e) => setNewSs({ ...newSs, inspection_frequency: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                >
                  <option value="MONTHLY">1 month</option>
                  <option value="QUARTERLY">3 months</option>
                  <option value="SIX_MONTHLY">6 months</option>
                  <option value="ANNUAL">12 months</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowAddSs(false);
                  setNewSs({ ss_code: '', location: '', inspection_frequency: 'ANNUAL' });
                }}
                className="px-3 py-1.5 text-sm border rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAdd}
                disabled={addSsMutation.isPending}
                className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg disabled:opacity-50"
              >
                {addSsMutation.isPending ? 'Adding…' : 'Add'}
              </button>
            </div>
          </div>
        )}

        {instances.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4">
            No SS yet. Upload a Compliance Schedule or click Add SS.
          </p>
        ) : (
          <ul className="divide-y divide-green-200 rounded-lg border border-green-300 bg-green-100">
            {instances.map((inst) => {
              const ssName =
                ssCodes.find((s) => s.code === inst.ss_code)?.name ||
                inst.ss_name ||
                inst.location ||
                '';
              const freqLabel =
                { MONTHLY: '1m', QUARTERLY: '3m', SIX_MONTHLY: '6m', ANNUAL: '12m' }[
                  inst.inspection_frequency
                ] ||
                inst.inspection_frequency ||
                '—';
              const on = !!checks[inst.id];
              return (
                <li key={inst.id} className="flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-green-200">
                  <input
                    type="checkbox"
                    checked={on}
                    disabled={busy}
                    onChange={(e) => onCheckChange(inst.id, e.target.checked)}
                    aria-label={`Include ${inst.ss_code} in inspection`}
                    className="shrink-0 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <button
                    type="button"
                    onClick={() => navigate(`/buildings/${buildingId}/ss/${inst.id}`)}
                    className="min-w-0 flex-1 text-left rounded-md px-1 py-0.5 -mx-1 hover:bg-primary-50/80 focus:outline-none focus:ring-2 focus:ring-primary-300"
                    title="Open SS settings (Default IQP, frequency, CS fields)"
                  >
                    <div className="flex items-center gap-1">
                      <div className="min-w-0 flex-1">
                        <div>
                          <span className="font-semibold text-gray-900">
                            {formatSsLabel(inst.ss_code)}
                          </span>
                          {ssName ? <span className="ml-2 text-gray-600">— {ssName}</span> : null}
                          <span className="ml-2 text-xs text-gray-400">({freqLabel})</span>
                        </div>
                        {inst.location && ssName !== inst.location ? (
                          <p className="text-xs text-gray-500 truncate">{inst.location}</p>
                        ) : null}
                        <p className="text-[11px] mt-0.5 truncate">
                          {inst.default_worker_name ||
                          inst.default_assignee_name ||
                          inst.default_worker_id ? (
                            <span className="text-sky-800 font-medium">
                              Default IQP:{' '}
                              {inst.default_worker_name ||
                                inst.default_assignee_name ||
                                `Worker #${inst.default_worker_id}`}
                            </span>
                          ) : (
                            <span className="text-amber-700">Default IQP: not set</span>
                          )}
                        </p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm(`Remove ${inst.ss_code} from this building?`)) {
                        deleteSsMutation.mutate(inst.id);
                      }
                    }}
                    className="text-xs text-red-600 hover:underline shrink-0 px-1"
                  >
                    Remove
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

export default forwardRef(BuildingSsSection);
